import React, {useContext, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import cn from 'classnames';
import _ from 'lodash';

import Form from 'components/common/Form';
import Select from 'components/common/Form/Select';
import Datepicker from 'components/common/Form/Datepicker';
import Button, {ButtonStyles} from 'components/common/Form/Button';
import PhoneInput from 'components/common/Form/PhoneInput';
import Input from 'components/common/Form/Input';

import {isCompanyAdmin, isModerator, isRespondent, isTranslator} from 'constants/UserRoles';
import YearsOfExperience from 'constants/YearsOfExperience';
import BusinessRoles from 'constants/BusinessRoles';
import {convertToOptions} from 'helpers/SelectUtils';

import {UserContext} from 'context/UserContext';

import style from 'components/Welcome/WelcomePage.module.scss';
import {
    getTranslatedName,
    NATIONALITIES,
    TRANSLATE_LANGUAGES,
    TRANSLATE_NATIONALITIES,
} from 'components/common/listConstants';
import MultiSwitch from 'components/common/Form/MultiSwitch/MultiSwitch';
import MultiSelect from 'components/common/Form/MultiSelect/MultiSelect';

import OptionSlider from 'components/common/Form/OptionSlider/OptionSlider';
import QuestionMarkTooltip from 'components/common/QuestionMarkTooltip/QuestionMarkTooltip';

const CompanyRepresentativeForm = ({
    onChange,
    onNextStep,
    data,
    role,
    errors,
    onChangeCompany,
    companyData,
    isLastStep,
    otherRole,
    changeOtherRole,
    validate,
    isModal,
}) => {
    const {birthday, phone, languages, phoneCountryCode, firstName, lastName, nativeCountry, workExperience} = data;
    const {businessRole} = companyData;
    const {validatePhoneNumberResult, clearPhoneNumberValidation} = useContext(UserContext);
    useEffect(() => {
        if (validatePhoneNumberResult) clearPhoneNumberValidation();
    }, [phone, phoneCountryCode]);

    const displayTooltips = isRespondent(role);
    const isTranslatorOrModerator = isTranslator(role) || isModerator(role);

    const {t} = useTranslation();

    const notCustomRole = businessRole === 'director' || businessRole === 'owner';
    return (
        <div className={style.profileFormContainer}>
            <Form className={cn(style.form, {[style.wideForm]: isTranslatorOrModerator, [style.modal]: isModal})}>
                <div className={style.inlineFieldsContainer}>
                    <Input
                        label={t('welcome:form.labels.firstname')}
                        placeholder={t('welcome:form.placeholders.firstname')}
                        labelClassName={style.labelClassName}
                        value={firstName}
                        onChange={onChange.bind(this, 'firstName')}
                        error={t(errors.firstName)}
                        errorAbsolutePosition
                        withFocus
                        onBlur={validate.bind(this, data, 'firstName')}
                        firstCapitalized
                    />
                    <Input
                        label={t('welcome:form.labels.lastname')}
                        placeholder={t('welcome:form.placeholders.lastname')}
                        labelClassName={style.labelClassName}
                        value={lastName}
                        onChange={onChange.bind(this, 'lastName')}
                        error={t(errors.lastName)}
                        errorAbsolutePosition
                        onBlur={validate.bind(this, data, 'lastName')}
                        firstCapitalized
                    />
                </div>
                <div className={style.inlineFieldsContainer}>
                    <Datepicker
                        label={t('welcome:form.labels.date')}
                        value={birthday}
                        onChange={onChange.bind(this, 'birthday')}
                        error={t(errors.birthday)}
                        name='birthday'
                        maxDate={new Date().setFullYear(new Date().getFullYear() - 18)}
                        withDateFormatToUTC
                        labelClassName={style.labelClassName}
                        id='birthday'
                        onChangeField={_.noop}
                        customInputClassName={style.withoutMarginBottom}
                        onBlur={validate.bind(this, data, 'birthday')}
                    />
                    <PhoneInput
                        name='phone'
                        label={t('welcome:form.labels.phone')}
                        placeholder={t('welcome:text.phoneNumber')}
                        value={phone}
                        phoneCountryCode={phoneCountryCode}
                        onChange={onChange}
                        error={t(errors.phone)}
                        customTooltipText={displayTooltips && t('tooltips:phone')}
                        labelClassName={style.labelClassName}
                        className={style.withoutMarginBottom}
                        onBlur={validate.bind(this, data, 'phone')}
                    />
                </div>
                <div className={style.inlineFieldsContainer}>
                    <div className={style.businessRole}>
                        <span>{t('welcome:form.labels.businessRole')}</span>
                        <MultiSwitch
                            options={convertToOptions(BusinessRoles)}
                            value={businessRole}
                            onChange={onChangeCompany.bind(this, 'businessRole')}
                        />
                    </div>
                    {notCustomRole ? (
                        <div />
                    ) : (
                        <Input
                            label=' '
                            placeholder={t('welcome:form.placeholders.businessRole')}
                            wrapperClassName={style.inputWithoutLabel}
                            value={otherRole}
                            onChange={changeOtherRole}
                            error={t(errors.businessRole)}
                            onBlur={validate.bind(this, data, 'businessRole')}
                        />
                    )}
                </div>
                <div className={style.inlineFieldsContainer}>
                    <Select
                        label={t('welcome:form.labels.nationality')}
                        placeholder={t('welcome:form.placeholders.nationality')}
                        options={TRANSLATE_NATIONALITIES(NATIONALITIES, t)}
                        value={nativeCountry}
                        onChange={onChange.bind(this, 'nativeCountry')}
                        error={t(errors.nativeCountry)}
                        labelClassName={style.labelClassName}
                        onBlur={validate.bind(this, data, 'nativeCountry')}
                    />
                </div>
                {!isTranslator(role) && !isCompanyAdmin(role) && (
                    <div className={style.inlineFieldsContainer}>
                        <MultiSelect
                            label={t('welcome:form.labels.language')}
                            placeholder={t('welcome:form.placeholders.language')}
                            options={TRANSLATE_LANGUAGES(t)}
                            selected={languages}
                            onChange={onChange.bind(this, 'languages')}
                            error={t(errors.languages)}
                            labelClassName={style.labelClassName}
                            optionsClassName={style.optionsClassName}
                            onBlur={validate.bind(this, data, 'languages')}
                            halfSize={isTranslatorOrModerator}
                            getName={getTranslatedName}
                        />
                        <div className={cn(style.markWrapper, {[style.halfSize]: isTranslatorOrModerator})}>
                            <QuestionMarkTooltip
                                text={t('welcome:form.tooltips.languages')}
                                classes={{tooltip: style.tooltip}}
                                markClasses={style.questionMarkIcon}
                            />
                        </div>
                    </div>
                )}
                {isTranslatorOrModerator && (
                    <div className={style.inlineFieldsContainer}>
                        <OptionSlider
                            options={YearsOfExperience}
                            className={style.experience}
                            label={
                                isModerator(role)
                                    ? t('welcome:form.labels.moderExperience')
                                    : t('welcome:form.labels.transExperience')
                            }
                            width={41}
                            onChange={onChange.bind(this, 'workExperience')}
                            value={workExperience}
                            error={t(errors.workExperience)}
                            onBlur={validate.bind(this, data, 'workExperience')}
                        />
                    </div>
                )}
                {isLastStep && <div className={style.commonError}>{errors.common}</div>}
                <div className={style.buttonWrapper}>
                    <Button
                        text={isLastStep ? t('welcome:buttons.getStarted') : t('welcome:buttons.continue')}
                        btnStyle={ButtonStyles.primary}
                        onClick={onNextStep}
                    />
                </div>
            </Form>
        </div>
    );
};

export default CompanyRepresentativeForm;
